import { useState } from "react";
import { agentService } from "../../services/api";
import toast from "react-hot-toast";
import PartnerConfirmDialog from "./PartnerConfirmDialog";

const AgentJoinRequestModal = ({
  requests,
  onClose,
  onRequestAction,
}) => {
  const [processing, setProcessing] = useState({});
  // DECISION-037 — Decline rejects the agent's join request with no undo: the
  // row is removed by the parent and the agent would have to apply again. It
  // asks first, through the same dialog the agency surface already uses. Accept
  // stays one click (it is the non-destructive direction, and an accepted agent
  // can still be removed later — that removal already confirms).
  const [declineTarget, setDeclineTarget] = useState(null);

  const handleAccept = async (requestId) => {
    setProcessing((prev) => ({ ...prev, [requestId]: true }));
    try {
      const response = await agentService.respondToAgentRequest(requestId, "accept");
      if (response.success) {
        toast.success("Agent request accepted successfully");
        onRequestAction(requestId, "accepted");
      } else {
        toast.error(response.error || "Failed to accept request");
      }
    } catch (error) {
      console.error("Error accepting request:", error);
      toast.error("Failed to accept request. Please try again.");
    } finally {
      setProcessing((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDecline = async (requestId) => {
    setProcessing((prev) => ({ ...prev, [requestId]: true }));
    try {
      const response = await agentService.respondToAgentRequest(requestId, "decline");
      if (response.success) {
        toast.success("Agent request declined successfully");
        onRequestAction(requestId, "declined");
      } else {
        toast.error(response.error || "Failed to decline request");
      }
    } catch (error) {
      console.error("Error declining request:", error);
      toast.error("Failed to decline request. Please try again.");
    } finally {
      setProcessing((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  // The dialog's confirm: run the UNCHANGED handler, then close. handleDecline
  // swallows its own errors, so the dialog always closes exactly once.
  const confirmDecline = async () => {
    if (!declineTarget) return;
    await handleDecline(declineTarget.id);
    setDeclineTarget(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Agent Join Requests
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {requests.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <p className="text-gray-500">No pending agent join requests</p>
            </div>
          ) : (
            requests.map((request) => (
              <div
                key={request.id}
                className="flex items-center justify-between px-6 py-4 border border-[#F7D9A4] bg-[#FFF8EC] rounded-xl shadow-lg"
                style={{
                  boxShadow: "0px 4px 16px rgba(16, 11, 39, 0.08)",
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0">
                    {request.agent.profile_picture_url && (
                      <img 
                        src={request.agent.profile_picture_url} 
                        alt={request.agent.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          width="20"
                          height="20"
                          rx="4"
                          fill="url(#paint0_linear_2130_56168)"
                        />
                        <path
                          d="M10 7.5V12"
                          stroke="white"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                        />
                        <circle cx="10" cy="14" r="0.8" fill="white" />
                        <defs>
                          <linearGradient
                            id="paint0_linear_2130_56168"
                            x1="10"
                            y1="0"
                            x2="10"
                            y2="20"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#DBA948" />
                            <stop offset="0.0001" stopColor="#FFC46B" />
                            <stop offset="1" stopColor="#FFA318" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <span className="text-sm font-medium text-gray-800">
                        Agent "{request.agent.name}" has requested to join your agency.
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex items-center gap-4">
                        <span>Email: {request.agent.email}</span>
                        {request.agent.phone_number && (
                          <span>Phone: {request.agent.phone_number}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        {request.agent.position && (
                          <span>Position: {request.agent.position}</span>
                        )}
                        {request.agent.license_number && (
                          <span>License: {request.agent.license_number}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <span>Active Players: {request.agent.active_player_slots || 0}</span>
                        <span>Transfer Points: {request.agent.transfer_points_total || 0}</span>
                        <span>Pitches: {request.agent.pitches || 0}</span>
                      </div>
                      {request.agent.document_url && (
                        <div>
                          <a 
                            href={request.agent.document_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            View License Document
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleAccept(request.id)}
                    disabled={processing[request.id]}
                    className="bg-[#38AB75] px-4 py-2 rounded-xl flex items-center gap-2 cursor-pointer transition-colors disabled:opacity-50 text-white"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 4L6 11L3 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="font-bold text-sm">
                      {processing[request.id] ? "..." : "Accept"}
                    </span>
                  </button>
                  <button
                    onClick={() => setDeclineTarget(request)}
                    disabled={processing[request.id]}
                    className="bg-gray-500 px-4 py-2 rounded-xl flex items-center gap-2 cursor-pointer transition-colors disabled:opacity-50 text-white"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4L4 12M4 4L12 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="font-bold text-sm">
                      {processing[request.id] ? "..." : "Decline"}
                    </span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* DECISION-037 — the agency surface's existing confirm dialog (it
          portals to document.body at z-[100], so it sits above this modal's
          z-50 overlay). "declineRequest" copy follows the 4c shape; the
          subject name is resolved here so the dialog never has to guess. */}
      <PartnerConfirmDialog
        action={
          declineTarget
            ? {
                kind: "declineRequest",
                partner: {
                  name:
                    declineTarget.agent?.name ||
                    declineTarget.agent?.email ||
                    "this agent",
                },
              }
            : null
        }
        loading={Boolean(processing[declineTarget?.id])}
        onCancel={() =>
          processing[declineTarget?.id] ? null : setDeclineTarget(null)
        }
        onConfirm={confirmDecline}
      />
    </div>
  );
};

export default AgentJoinRequestModal; 